/**
 * Layout row types + renderer ids for `tento/tento-lona-js/sheets`.
 *
 * Each layout is its own fundamental type — `timeline[row]`,
 * `timeline[column]`, `grid`, etc. are NOT interchangeable. No
 * shared "timeline" abstraction.
 *
 * Renderers are named `<layout>.<renderer>`. Examples:
 * `column.text`, `canvas.hrule`, `timeline[row].event`,
 * `timeline[column].event`, `grid.event`.
 *
 * Detail: `sheets/.docs/content/rows-per-type.md`.
 */

import { Layout } from "../@generated/row-type-catalog.ts";
import type { Option } from "../core/option.ts";
import { deepFrozen } from "../core/frozen-collections.ts";

/**
 * A layout row type: the `ty` of a row that arranges its children
 * (`column`, `canvas`, `timeline[row]`, `timeline[column]`, `grid`,
 * `calendar-body`): the generated row-type catalog's layout types
 * (`RowTypeCatalog.Layout`, mirrored from the server's row-type taxonomy),
 * spelled out and held equal to it by `sheets/tests/layout-kind/layout-kind.deno.test.ts`;
 * `LayoutKind.values` lists them in the catalog's order.
 */
export type LayoutKind = "column" | "grid" | "timeline[row]" | "timeline[column]" | "canvas" | "calendar-body";

/** Values that go with `LayoutKind`: `values`. */
export namespace LayoutKind {
  /** Every layout row type, in the catalog's order. */
  export const values: readonly LayoutKind[] = deepFrozen(Object.values(Layout));
}

/** A slot-qualified renderer id: a layout row type, `.`, the renderer kind. */
export type RendererId = `${LayoutKind}.${string}`;

/**
 * Parse a slot-qualified renderer id (`timeline[column].timeline-legend`)
 * into its layout slot + renderer kind. Splits at the FIRST `.` — surfaces
 * contain `[`/`]` but never `.`, so the prefix is unambiguous. Returns null
 * when the prefix is not a known `LayoutKind` value or the kind is empty.
 */
export function parseRendererId(
  ty: string,
): Option<{ slot: LayoutKind; kind: string }> {
  const dot = ty.indexOf(".");
  if (dot < 0) return null;
  const prefix = ty.slice(0, dot);
  const kind = ty.slice(dot + 1);
  if (kind.length === 0) return null;
  const slot = LayoutKind.values.find((v) => v === prefix);
  if (slot === undefined) return null;
  return { slot, kind };
}

/**
 * `ty` as a `RendererId` when it is one (see `parseRendererId`: a known
 * layout prefix and a non-empty renderer kind), else `null`.
 */
export function rendererIdOf(ty: string): Option<RendererId> {
  const parsed = parseRendererId(ty);
  return parsed == null ? null : `${parsed.slot}.${parsed.kind}`;
}
